import type { BulkOverrideResult } from "@mailhand/core/api";
import { type ActionId, LIMITS, type MessageId } from "@mailhand/core/schemas";
import type { ApiClient } from "../../api/client.js";

export type BulkOutcome = BulkOverrideResult["results"][number];

/** Sends the messages in chunks that the API accepts, one chunk after the other. */
export async function overrideMessages(api: ApiClient, messageIds: readonly MessageId[], actionId: ActionId, needsApproval: boolean): Promise<BulkOutcome[]> {
  const results: BulkOutcome[] = [];
  for (let start = 0; start < messageIds.length; start += LIMITS.bulkOverride) {
    const chunk = messageIds.slice(start, start + LIMITS.bulkOverride);
    results.push(...(await api.call("messagesOverride", { body: { messageIds: chunk, actionId, needsApproval } })).results);
  }
  return results;
}

function countOf(count: number): string {
  return count === 1 ? "1 message" : `${count} messages`;
}

/** For example: "Dismissed 12 messages. Skipped 2: This message is still being handled. Try again in a moment." */
export function bulkSummary(verb: string, results: readonly BulkOutcome[]): string {
  const changed = results.filter((result) => result.outcome === "changed").length;
  const reasons = [...new Set(results.flatMap((result) => (result.outcome === "skipped" ? [result.reason.endsWith(".") ? result.reason : `${result.reason}.`] : [])))];
  const skipped = results.length - changed;
  const done = `${verb} ${countOf(changed)}.`;
  return skipped === 0 ? done : `${done} Skipped ${skipped}: ${reasons.join(" ")}`;
}
